import { useState, useCallback } from 'react';
import QRCode from 'qrcode';
import { Download } from 'lucide-react';

const DEFAULT_PLACEHOLDER = '輸入文字或網址，例如 https://example.com';
const MAX_LENGTH = 2000;
const QR_OPTIONS: QRCode.QRCodeToDataURLOptions = {
  width: 256,
  margin: 2,
  errorCorrectionLevel: 'M',
};

/**
 * QR 產生器工具：輸入文字/網址即時產生 QR Code，可下載 PNG
 */
export function QrGeneratorTool() {
  const [input, setInput] = useState('');
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);

  const generate = useCallback(async () => {
    const text = input.trim();
    if (!text) {
      setError('請輸入文字或網址');
      setDataUrl(null);
      return;
    }
    if (text.length > MAX_LENGTH) {
      setError(`內容不可超過 ${MAX_LENGTH} 字`);
      setDataUrl(null);
      return;
    }
    setError(null);
    setGenerating(true);
    try {
      const url = await QRCode.toDataURL(text, QR_OPTIONS);
      setDataUrl(url);
    } catch (_e) {
      setError('產生失敗，請稍後再試');
      setDataUrl(null);
    } finally {
      setGenerating(false);
    }
  }, [input]);

  const handleDownload = useCallback(() => {
    if (!dataUrl) return;
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = 'qrcode.png';
    a.click();
  }, [dataUrl]);

  return (
    <>
      {/* 核心操作區 */}
      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <label
          htmlFor="qr-input"
          className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          文字或網址
        </label>
        <textarea
          id="qr-input"
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder={DEFAULT_PLACEHOLDER}
          rows={3}
          maxLength={MAX_LENGTH}
          className="mb-3 w-full rounded border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder-gray-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
        />
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={generate}
            disabled={generating}
            className="rounded bg-primary px-4 py-2 font-medium text-white hover:bg-primary/90 disabled:opacity-50"
          >
            {generating ? '產生中…' : '產生 QR Code'}
          </button>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {input.length} / {MAX_LENGTH}
          </span>
        </div>
        {error && (
          <p
            className="mt-2 text-sm text-red-600 dark:text-red-400"
            role="alert"
          >
            {error}
          </p>
        )}
      </div>

      {/* 結果輸出區 */}
      <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-6 dark:border-gray-700 dark:bg-gray-800">
        <h2 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
          預覽
        </h2>
        {dataUrl ? (
          <div className="flex flex-col items-start gap-3">
            <img
              src={dataUrl}
              alt="QR Code"
              className="rounded border border-gray-200 bg-white p-2 dark:border-gray-600"
            />
            <button
              type="button"
              onClick={handleDownload}
              className="inline-flex items-center gap-2 rounded bg-primary px-4 py-2 font-medium text-white hover:bg-primary/90"
            >
              <Download className="h-4 w-4" />
              下載 PNG
            </button>
          </div>
        ) : (
          <p className="text-gray-500 dark:text-gray-400">
            輸入內容後點擊「產生 QR Code」即可顯示預覽。
          </p>
        )}
      </div>

      {/* SEO 區塊 */}
      <section className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <h2 className="mb-3 text-lg font-semibold text-gray-900 dark:text-gray-100">
          使用說明
        </h2>
        <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
          QR Code
          可儲存網址、純文字或聯絡資訊，手機掃描即可開啟。本工具在瀏覽器內即時產生，不會上傳您的內容。支援中文與多國語言，長度建議不超過
          2000 字以維持掃描成功率。產生後可下載 PNG 圖片用於列印或分享。
        </p>
      </section>
    </>
  );
}
